/* eslint-disable @typescript-eslint/consistent-type-exports */
/* eslint-disable @typescript-eslint/naming-convention */
import {type SingleAlgoFullReport} from './algo/fe-algo';
import {type QEPSharePublishData} from './linker';
import {type OrderRecord} from './order';
import {type ApiServicesType} from './qep-system/api-services';
import {type ConnectivityType} from './qep-system/connectivity';
import {type ContainerType} from './qep-system/container';
import {type LoggerType} from './qep-system/logger';
import {type NotificationType} from './qep-system/notification';
import {type UtilsType} from './qep-system/utils';
import {type WorkersType} from './qep-system/workers';

// eslint-disable-next-line @typescript-eslint/no-restricted-types -- System metadata explicitly returns a null host-info sentinel.
type NullValue = null;

/** Carries queryable custom attributes without exposing a Datadog SDK dependency to feature packages. */
export type RumTelemetryContext = Readonly<Record<string, unknown>>;

/** Defines the bounded Datadog RUM operations exposed by the QEP preload singleton. */
export type RumTelemetryType = {
  /** Records a named user or application action with optional queryable context. */
  addAction: (name: string, context?: RumTelemetryContext) => void;
  /** Captures an error with optional queryable context. */
  addError: (error: unknown, context?: RumTelemetryContext) => void;
  /** Records a named view-relative timing at the current instant. */
  addTiming: (name: string) => void;
};

export type QEPSystemType = {
  apiServices: ApiServicesType;
  checkIfInTesting: () => Promise<void>;
  connectivity: ConnectivityType;
  container: ContainerType;
  loadContainer: () => void;
  logger: LoggerType;
  notification: NotificationType;
  onLoad: () => void;
  rum: RumTelemetryType;
  utils: UtilsType;
  workers: WorkersType;

  /**
   * @deprecated - unused
   */
  auth: {logout: () => void};
  /**
   * @deprecated - unused
   */
  getVersion: () => Promise<{version?: string; gitCommit?: string; finsembleVersion?: string; systemInfo: NullValue}>;
  /**
   * @deprecated - unused
   */
  gridColumnApi?: unknown;
};

export enum QEPNotificationStatus {
  CRITICAL = 'CRITICAL',
  HIGH = 'HIGH',
  MEDIUM = 'MEDIUM',
  LOW = 'LOW',
  SYSTEM = 'SYSTEM',
}

export enum QEPNotificationType {
  ORDER = 'ORDER',
  ALGO = 'ALGO',
  SYSTEM = 'SYSTEM',
  ALERT = 'ALERT',
}

export enum QEPNotificationActionType {
  SHARE = 'SHARE',
  QEP_ACTION = 'QEP_ACTION',
}

export enum QEPSystemAction {
  OPEN_ISSUE_REPORTER = 'OPEN_ISSUE_REPORTER',
  LOGOUT = 'LOGOUT',
}

export type QEPNotificationAction<T> = {
  type: T;
  label: T extends QEPNotificationActionType.QEP_ACTION ? undefined : string;
  data: T extends QEPNotificationActionType.SHARE ? QEPSharePublishData<any, any> : QEPSystemAction;
};

export type QEPNotification<T> = {
  id?: string;
  type: QEPNotificationType;
  title: string;
  shortDescription: string;
  longDescription?: string;
  payload?: OrderRecord | SingleAlgoFullReport;
  isRead?: boolean;
  hasNotified?: boolean;
  status: QEPNotificationStatus;
  timestamp: string;
  actions?: Array<QEPNotificationAction<T>>;
};

export enum QEPNotificationLocation {
  WINDOW = 'WINDOW', APPLICATION = 'APPLICATION',
}

export type QEPNotificationPayload<T> = {
  id?: string;
  title: string;
  shortDescription?: string;
  longDescription?: string;
  type: QEPNotificationType;
  status?: QEPNotificationStatus;
  timestamp?: string;
  payload?: OrderRecord | SingleAlgoFullReport;
  location?: QEPNotificationLocation;
};

export * from './qep-system/api-services';
export * from './qep-system/application-client';
export * from './qep-system/authentication';
export * from './qep-system/base-api';
export * from './qep-system/connectivity';
export * from './qep-system/container';
export * from './qep-system/errors';
export * from './qep-system/linker-api';
export * from './qep-system/logger';
export * from './qep-system/notification';
export * from './qep-system/pubsub';
export * from './qep-system/utils';
export * from './qep-system/worker-messages';
export * from './qep-system/workers';
